import { MyTooltip } from "@/components/my-ui/shared/Tooltip";
import { Package } from "@/types/packages/data-only/DataOnlyCountryPackages";
import {
  Antenna,
  Calendar,
  Database,
  Dot,
  MessageCircle,
  Phone,
  Rocket,
} from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import PackageCoverage from "./PackageCoverage";
import PackageDetailItem from "./PackageDetailItem";

interface PropsType {
  packageDetail: Package;
  countryFlag: string;
}

function PackageDetail({ packageDetail, countryFlag }: PropsType) {
  const t = useTranslations(`Shared.countryPackages.packageDetailModal`);

  const {
    name: packageName,
    data_quantity,
    data_unit,
    package_validity,
    package_validity_unit,
    throttling,
    tether,
    sms_quantity,
    voice_quantity,
    voice_unit,
    provider,
    package_type,
    activation_type_description,
    other_info,
    unthrottle_data,
    throttle_speed,
    international_minutes,
    international_sms,
  } = packageDetail;

  const isOrangeProvider = provider === "Orange";
  const isEsimNetProvider = provider === "Esim.net";
  const isUnlimitedSms = sms_quantity < 0;

  const isInternationalMinutes = isOrangeProvider && international_minutes > 0;
  const isInternationlSms = isOrangeProvider && international_sms > 0;

  // const providers = ["ultimate_mobile", "Phonico"];

  return (
    <>
      {/* flag and package name  */}
      <div className="mt-2 flex items-center gap-6">
        <Image
          src={countryFlag}
          alt="Global Packages"
          width={38}
          height={38}
          sizes="auto"
          className="shrink-0 rounded-full object-cover shadow-md"
        />
        <p className="text-start text-base font-500 leading-none xl:text-body-lg xl:leading-normal">
          {packageName}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-3.5">
        {/* data quantity  */}
        <PackageDetailItem>
          <div className="flex items-center gap-2">
            <Database size={19} className="text-primary" />
            <p className="font-500">{t(`data`)}</p>
          </div>
          <p>
            {data_quantity === -1 ? "Unlimited" : data_quantity} {data_unit}
          </p>
        </PackageDetailItem>

        {/* validity  */}
        <PackageDetailItem>
          <div className="flex items-center gap-2">
            <Calendar size={19} className="text-primary" />
            <p className="text-base font-500">{t(`validity`)}</p>
          </div>
          <p>
            {package_validity} {package_validity_unit}
          </p>
        </PackageDetailItem>

        {/* throttling  */}
        <PackageDetailItem>
          <div className="flex items-center gap-2">
            <Rocket size={19} className="text-primary" />
            <p className="text-base font-500">{t(`speedLimit.title`)}</p>
          </div>
          <p className="flex items-center gap-2">
            {throttling ? (
              <>
                <span>{t(`speedLimit.yes`)}</span>
                {throttle_speed && unthrottle_data && (
                  <MyTooltip
                    message={t(`speedLimit.tooltip`, {
                      unthrottle_data,
                      throttle_speed,
                    })}
                  />
                )}
              </>
            ) : (
              "No"
            )}
          </p>
        </PackageDetailItem>

        {/* Tethering Hotspot  */}
        <PackageDetailItem>
          <div className="flex items-center gap-2">
            <Antenna size={19} className="text-primary" />
            <p className="text-base font-500">{t(`tethering.title`)}</p>
          </div>
          <p>{tether ? t(`tethering.yes`) : t(`tethering.no`)}</p>
        </PackageDetailItem>

        {package_type === "DATA-VOICE-SMS" && (
          <>
            {/* minutes quantity  */}
            {voice_quantity !== 0 && (
              <PackageDetailItem>
                <div className="flex items-center gap-2">
                  <Phone size={19} className="text-primary" />
                  <p className="text-base font-500">{voice_unit}</p>
                </div>
                <p>
                  {voice_quantity < 0 ? t(`minutes.unlimited`) : voice_quantity}
                </p>
              </PackageDetailItem>
            )}

            {/* sms quantity  */}
            {sms_quantity !== 0 && (
              <PackageDetailItem>
                <div className="flex items-center gap-2">
                  <MessageCircle size={19} className="text-primary" />
                  <p className="text-base font-500">{t("sms.title")}</p>
                </div>
                <p>
                  {isEsimNetProvider && t("sms.incomingOnly")}
                  {!isEsimNetProvider && isUnlimitedSms && t("sms.unlimited")}
                  {!isEsimNetProvider && !isUnlimitedSms && sms_quantity}
                </p>
              </PackageDetailItem>
            )}
          </>
        )}

        {isInternationalMinutes && (
          <PackageDetailItem>
            <div className="flex items-center gap-2">
              <Phone size={19} className="text-primary" />
              <p className="text-base font-500">International Minutes</p>
            </div>
            <p>{international_minutes}</p>
          </PackageDetailItem>
        )}

        {isInternationlSms && (
          <PackageDetailItem>
            <div className="flex items-center gap-2">
              <MessageCircle size={19} className="text-primary" />
              <p className="text-base font-500">International SMS</p>
            </div>
            <p>{international_sms}</p>
          </PackageDetailItem>
        )}
      </div>

      <PackageCoverage id={packageDetail.id} provider={provider} />

      {/* message of activation  */}
      <ul className="flex flex-col rounded-[9px] border border-primary p-1">
        <li className="flex text-sm">
          <Dot size={18} className="shrink-0" />
          {activation_type_description}
        </li>
        {other_info &&
          other_info.split("\\n").map((item, index) => (
            <li className="flex text-sm" key={index}>
              <Dot size={18} className="shrink-0" />
              {item}
            </li>
          ))}
      </ul>
    </>
  );
}

export default PackageDetail;
